import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { auth } from '../api/authApi';
import { Loader } from '@mantine/core';
import './Auth.css';
import LogoSvg from '../assets/logo.svg';

interface PasswordResetFormProps {
  onSwitchToLogin: () => void;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await auth.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="auth-container">
        <header className="auth-header-bar">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 40, width: 40, display: 'block', marginRight: 10 }} />
            <h1 style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-1px', margin: 0, textAlign: 'center', lineHeight: '40px' }}>SymphonyTodo</h1>
          </div>
        </header>
        <main className="auth-content-section">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Check Your Email</h1>
              <p>We've sent a password reset link to {email}</p>
            </div>
            <div className="reset-success-message">
              <p>Click the link in the email to reset your password.</p>
              <p>The link will expire in 1 hour.</p>
            </div>
            <button 
              className="auth-link"
              onClick={onSwitchToLogin}
            >
              Back to Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <header className="auth-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 40, width: 40, display: 'block', marginRight: 10 }} />
          <h1 style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-1px', margin: 0, textAlign: 'center', lineHeight: '40px' }}>SymphonyTodo</h1>
        </div>
      </header>
      <main className="auth-content-section">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
            <p>Enter your email and we'll send you a reset link</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                  autoCorrect="off"
                  spellCheck="false"
                />
              </div>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading || !email.trim()}>
              {loading ? <Loader type="bars" size="sm" color="white" /> : 'Send Reset Link'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <button 
                className="auth-link"
                onClick={onSwitchToLogin}
              >
                Sign In
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};
